import React, { useEffect, useMemo, useRef, useState } from "react";
import { NotificationManager } from "react-notifications";
import { FetchApiError } from "../../../../../api/api";
import TemplateApi, {
  Template,
  normalizeTemplateUseValue,
  TEMPLATE_USE_GENERAL,
} from "../../../../../store/reducers/builderReducers/templates/templateApi";
import Select from "react-select";
import { DefaultReactSelectStyle } from "../../../ReactSelectStyle/ReactSelectStyle";
import { useAppSelector } from "../../../../../store/hooks";
import {
  activeLanguagesForTemplateForms,
  resolveTemplateFormLanguageSelectValue,
} from "../../../../../store/reducers/builderReducers/templates/templateHelpers";
import {
  displayNameFromActiveLanguage,
  languageCodeFromActiveLanguage,
  languageCodesEqual,
} from "../../../../../utils/languageCode";
import {
  activeLanguageRowFlagUrl,
  cssBackgroundUrlFromLanguageSelectIcon,
} from "../../../../_default/icons/languageFlagIcons";

function templateUseFromComponentOrStoredUse(template: Template): string {
  const kind = template.component?.type;
  if (kind === "header") return "header";
  if (kind === "footer") return "footer";
  return normalizeTemplateUseValue(template.use);
}

interface Props {
  template: Template;
  setGeneralEdit: any;
  setEditTemplate: any;
}

export default function TemplateDetailsGeneralEditForm({
  template,
  setGeneralEdit,
  setEditTemplate,
}: Props) {
  const rawActiveLanguages = useAppSelector(
    (state) => state.builder.languageSettings.activeLanguages
  );

  const languages = useMemo(
    () => activeLanguagesForTemplateForms(rawActiveLanguages),
    [rawActiveLanguages]
  );

  const templateLanguageOptions = useMemo(
    () =>
      languages.map((x) => {
        const value = languageCodeFromActiveLanguage(x);
        return {
          label: displayNameFromActiveLanguage(x),
          value,
          locale: value,
          icon: activeLanguageRowFlagUrl(x),
        };
      }),
    [languages]
  );

  const templateKey =
    template.rootComponentUuid?.trim() ??
    `draft:${template.name}:${String(template.languageCode ?? "")}`;

  const prevTemplateKeyRef = useRef<string | null>(null);

  const [cachedTemplateInfo, setCachedTemplateInfo] = useState({
    name: template.name,
    languageCode: "",
    use: templateUseFromComponentOrStoredUse(template),
  });

  useEffect(() => {
    const keyChanged = prevTemplateKeyRef.current !== templateKey;
    prevTemplateKeyRef.current = templateKey;

    const nextUse = templateUseFromComponentOrStoredUse(template);

    if (keyChanged) {
      setCachedTemplateInfo({
        name: template.name,
        languageCode: resolveTemplateFormLanguageSelectValue(
          template,
          languages
        ),
        use: nextUse,
      });
      return;
    }

    if (!languages.length) return;

    setCachedTemplateInfo((prev) => {
      const prevOk = languages.some((row) =>
        languageCodesEqual(
          languageCodeFromActiveLanguage(row),
          prev.languageCode
        )
      );
      if (prevOk) return prev;
      return {
        ...prev,
        languageCode: resolveTemplateFormLanguageSelectValue(
          template,
          languages
        ),
      };
    });
  }, [
    templateKey,
    template.name,
    template.languageCode,
    template.use,
    template.component?.type,
    languages,
  ]);

  const isCreateMode = !template.rootComponentUuid?.trim();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);

  function handleSubmit(evt: React.FormEvent) {
    evt.preventDefault();

    if (submitInFlightRef.current) {
      return;
    }

    if (!String(cachedTemplateInfo.languageCode ?? "").trim()) {
      NotificationManager.error(
        languages.length
          ? "Please select a language."
          : "No active languages are configured for this site.",
        null,
        5000
      );
      return;
    }

    const payload = {
      ...template,
      ...cachedTemplateInfo,
      visible: true,
      use: normalizeTemplateUseValue(cachedTemplateInfo.use),
    };

    const request = isCreateMode
      ? TemplateApi.createTemplate(payload)
      : TemplateApi.saveTemplateById(payload);

    submitInFlightRef.current = true;
    setIsSubmitting(true);
    request
      .then(() => {
        NotificationManager.success(
          isCreateMode
            ? "Template created successfully."
            : "Template saved successfully.",
          null,
          3000
        );
        setGeneralEdit(false);
        setEditTemplate(payload);
      })
      .catch((error: unknown) => {
        const message =
          error instanceof FetchApiError
            ? error.message
            : error instanceof Error
              ? error.message
              : isCreateMode
                ? "Could not create template."
                : "Could not save template.";
        NotificationManager.error(message, null, 5000);
      })
      .finally(() => {
        submitInFlightRef.current = false;
        setIsSubmitting(false);
      });
  }

  const DefaultReactSelectStyle_ = {
    ...DefaultReactSelectStyle,
    valueContainer: (styles: any) => {
      return {
        ...styles,
        paddingLeft: "0px",
        paddingRight: "0px",
      };
    },
  };

  const languageSelectDot = (icon: string) => ({
    alignItems: "center",
    display: "flex",
    ":before": {
      background: cssBackgroundUrlFromLanguageSelectIcon(icon),
      content: '" "',
      display: "block",
      marginRight: 8,
      height: 14,
      width: 24,
      backgroundSize: "24px 14px",
      flexShrink: 0,
    },
  });

  const templateLanguageSelectStyle = {
    ...DefaultReactSelectStyle_,
    singleValue: (styles: any, { data }: any) => ({
      ...styles,
      ...languageSelectDot(String(data.icon ?? "")),
      color: "#fff",
      fontSize: "12px",
    }),
    option: (styles: any, data: any) => ({
      ...DefaultReactSelectStyle.option!(styles, data),
      fontSize: "12px",
      color: "#fff",
      ...languageSelectDot(String(data.data.icon ?? "")),
    }),
  };

  const templateUseOptions: any = [
    {
      label: "Checkout terms",
      value: "checkoutTerms",
      url: "",
    },
    {
      label: "Cookie Policy",
      value: "cookiePolicy",
      url: "",
    },
    {
      label: "Footer",
      value: "footer",
      url: "",
    },
    {
      label: "General",
      value: TEMPLATE_USE_GENERAL,
      url: "",
    },
    {
      label: "Header",
      value: "header",
      url: "",
    },
    {
      label: "Mega Menu",
      value: "megaMenu",
      url: "",
    },
    {
      label: "Mobile Menu",
      value: "mobileMenu",
      url: "",
    },
  ];

  return (
    <form onSubmit={handleSubmit}>
      <div className="b-row b-mt-3">
        <div className="col-12">
          <label className="fs-small">Name</label>
          <input
            className="form-input builder-input"
            value={cachedTemplateInfo.name}
            onChange={(evt) =>
              setCachedTemplateInfo({
                ...cachedTemplateInfo,
                name: evt.target.value,
              })
            }
          />
        </div>
      </div>
      <div className="b-row b-mt-3">
        <div className="col-12">
          <label className="fs-small">Use</label>
          <Select
            isSearchable={false}
            options={templateUseOptions}
            value={templateUseOptions.find(
              (x: any) => x.value === cachedTemplateInfo.use
            )}
            onChange={(option) => {
              setCachedTemplateInfo({
                ...cachedTemplateInfo,
                use: option!.value,
              });
            }}
            styles={DefaultReactSelectStyle_}
          ></Select>
        </div>
      </div>

      <div className="col-12 b-mt-3">
        <label className="fs-small">Language</label>

        <Select
          isSearchable={false}
          options={templateLanguageOptions}
          value={templateLanguageOptions.find((x) =>
            languageCodesEqual(x.value, cachedTemplateInfo.languageCode)
          )}
          onChange={(option) => {
            setCachedTemplateInfo({
              ...cachedTemplateInfo,
              languageCode: option!.value,
            });
          }}
          styles={templateLanguageSelectStyle}
          required
          noOptionsMessage={() => ""}
          placeholder="Select language"
        ></Select>
      </div>

      <div className="b-my-3">
        <button
          className="sidebar-save-btn"
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Saving…" : "Save"}
        </button>
        <a
          className="b-px-3 hover-underline sidebar-cancel-btn"
          onClick={() => {
            if (isSubmitting) return;
            setGeneralEdit(false);
          }}
          style={isSubmitting ? { pointerEvents: "none", opacity: 0.6 } : undefined}
        >
          Cancel
        </a>
      </div>
    </form>
  );
}
